import {
  BoundMultiSelectField,
  BoundRadioGroupField,
  BoundSelectField,
  Css,
  FormLines,
  FormSectionLayout,
  MultiSelectField,
  useComputed,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useTradePartnersSelectQuery } from "src/components/autoPopulateSelects/TradePartnerSelectField.generated";
import { CostType, InternalEstimateType, ReadyPlanStatus } from "src/generated/graphqlTypes";
import {
  PlanBasedBidPackageDetailsStepQuery,
  usePlanBasedBidPackageDetailsStep_CommunitiesQuery,
  usePlanBasedBidPackageDetailsStep_ProductOfferingsQuery,
  usePlanBasedBidPackageDetailsStepQuery,
} from "src/routes/bids/plan-based-bid-package/components/1.PlanBasedBidPackageDetailsStep.generated";
import { PlanBasedBidPackagePage_OriginatingProjectFragment } from "src/routes/bids/plan-based-bid-package/PlanBasedBidPackagePage.generated";
import {
  INTERNAL_ESTIMATE_ID,
  internalEstimateTypeOptions,
  PlanBasedBidPackageFormState,
} from "src/routes/bids/plan-based-bid-package/utils";
import { queryResult } from "src/utils";

type PlanBasedBidPackageDetailsStepProps = {
  formState: ObjectState<PlanBasedBidPackageFormState>;
  originatingProject?: PlanBasedBidPackagePage_OriginatingProjectFragment;
};

export function PlanBasedBidPackageDetailsStep(props: PlanBasedBidPackageDetailsStepProps) {
  const { formState, originatingProject } = props;
  const query = usePlanBasedBidPackageDetailsStepQuery();
  return queryResult(query, (data) => (
    <PlanBasedBidPackageDetailsView {...data} formState={formState} originatingProject={originatingProject} />
  ));
}

function PlanBasedBidPackageDetailsView(
  props: PlanBasedBidPackageDetailsStepQuery & PlanBasedBidPackageDetailsStepProps,
) {
  const { formState, markets, costCodes, originatingProject } = props;
  const { isNew, selectedMarketId, selectedCostCodes, selectedCommunities, selectedTradePartnerIds } = useComputed(
    () => ({
      isNew: !formState.bidPackageId.value,
      selectedMarketId: formState.marketId.value,
      selectedCostCodes: formState.costCodes.value,
      selectedCommunities: formState.projects.value ?? [],
      selectedTradePartnerIds: formState.requests.value
        .filter((r) => !r.archive)
        .map((r) => r.tradePartnerId)
        .compact(),
    }),
    [formState],
  );
  const { data: tradePartnersData } = useTradePartnersSelectQuery({
    variables: { filter: { coversCostCodes: selectedCostCodes, markets: [selectedMarketId].compact() } },
    skip: !selectedCostCodes || selectedCostCodes.isEmpty,
    fetchPolicy: "cache-first",
  });
  const { data: productOfferingsData } = usePlanBasedBidPackageDetailsStep_ProductOfferingsQuery({
    variables: { filter: { project: selectedCommunities, status: [ReadyPlanStatus.Active] } },
    skip: selectedCommunities.isEmpty,
  });
  const { data: communitiesData } = usePlanBasedBidPackageDetailsStep_CommunitiesQuery({
    variables: { marketId: selectedMarketId! },
    skip: !selectedMarketId,
  });

  return (
    <FormSectionLayout
      title="Define the Bid Package"
      description="Choose the Product Offerings and Cost Codes for this bid."
      initialFields={
        <FormLines labelStyle="above" width="full">
          {!originatingProject && (
            <BoundSelectField
              required
              options={markets}
              field={formState.marketId}
              onSelect={(option) => {
                formState.projects.set([]);
                formState.marketId.set(option);
              }}
            />
          )}
          <BoundMultiSelectField
            label="Communities"
            options={communitiesData?.communities.entities ?? []}
            disabled={selectedMarketId ? undefined : "Please select a market"}
            field={formState.projects}
            required
            onSelect={(communityIds) => {
              formState.projects.set(communityIds);
              // Prefill product offerings from the picked communities, but only on the
              // first pick. After that the user owns the selection
              if (isNew && formState.productOfferings.value?.isEmpty) {
                const pofs = (communitiesData?.communities.entities ?? [])
                  .filter((c) => communityIds.includes(c.id))
                  .flatMap((c) =>
                    c.productOfferings.filter((po) => po.status.code === ReadyPlanStatus.Active).map((po) => po.id),
                  );
                formState.productOfferings.set(pofs);
              }
            }}
          />
          <BoundMultiSelectField
            label="Product Offerings"
            options={productOfferingsData?.productOfferings.entities ?? []}
            disabled={selectedCommunities.isEmpty ? "Please select at least one community" : undefined}
            field={formState.productOfferings}
            multiline
          />
          <div css={Css.gray700.$}>
            All plans in selected communities are included by default. Remove any plans that should not be part of this
            bid.
          </div>
          <BoundMultiSelectField field={formState.costCodes} options={costCodes} label="Cost Codes" multiline />
          <BoundSelectField
            field={formState.costType}
            getOptionValue={(ct) => ct.code}
            getOptionLabel={(ct) => ct.name}
            options={[
              { code: CostType.Labor, name: "Labor" },
              { code: CostType.Materials, name: "Material" },
              { code: CostType.Subcontractor, name: "Subcontractor" },
            ]}
          />
          <MultiSelectField
            label="Trades (optional)"
            values={selectedTradePartnerIds}
            options={[
              ...(tradePartnersData?.tradePartners ?? []),
              { name: "Homebound Internal Estimate", id: INTERNAL_ESTIMATE_ID },
            ]}
            onSelect={(newIds) => {
              // Reuse existing rows when selected (so re-saves upsert),
              // archive persisted rows that were deselected.
              const current = formState.requests.value ?? [];
              formState.requests.set([
                ...newIds.map((tradePartnerId) => ({
                  ...current.find((r) => r.tradePartnerId === tradePartnerId),
                  tradePartnerId,
                  archive: false,
                })),
                ...current
                  .filter((r) => r.id && r.tradePartnerId && !newIds.includes(r.tradePartnerId))
                  .map((r) => ({ ...r, archive: true })),
              ]);

              // Default estimate type to Provisional if Homebound Internal Estimate is selected
              if (newIds.includes(INTERNAL_ESTIMATE_ID) && !formState.internalEstimateType.value) {
                formState.internalEstimateType.set(InternalEstimateType.Provisional);
              }
            }}
            multiline
            data-testid="tradePartnerIds"
          />
          {selectedTradePartnerIds.includes(INTERNAL_ESTIMATE_ID) && (
            <BoundRadioGroupField
              required
              label="Estimate Type"
              options={internalEstimateTypeOptions}
              field={formState.internalEstimateType}
            />
          )}
        </FormLines>
      }
    />
  );
}
